import React from "react";
import { TextField, Tooltip } from "@material-ui/core";
import {
	DrawerBody,
	DrawerCloseBtn,
	DrawerDefaultBlock,
	DrawerFooter,
	DrawerFooterBtn,
	DrawerHeader,
	DrawerKeyBreadcrumb,
	DrawerStatusRow,
	DrawerTabBar,
	DrawerTabBtn,
	DrawerTitle,
	DrawerTitleGroup,
	StatusChip,
	StyledDrawer,
} from "../styles";
import { LABELS } from "../labels";
import DrawerStyleTab from "./DrawerStyleTab";
import DrawerHistoryTab from "./DrawerHistoryTab";
import DrawerContextTab from "./DrawerContextTab";
import DrawerValidationPanel from "./DrawerValidationPanel";
import TagsCell from "./TagsCell";
import { validateEntry } from "../validators";
import type { DrawerTab, GlossaryTerm, StationsTextEntry, StationsTextPaletteEntry, TextRegistryScope } from "../types";

interface Props {
	langs: string[];
	open: boolean;
	entry: (StationsTextEntry & { tags?: string[] }) | null;
	activeTab: DrawerTab;
	scope: TextRegistryScope;
	palette: StationsTextPaletteEntry[];
	glossary: GlossaryTerm[];
	dirty: boolean;
	saving: boolean;
	onClose: () => void;
	onTabChange: (tab: DrawerTab) => void;
	onEntryChange: (patch: Partial<StationsTextEntry>) => void;
	onRevert: (versionId: number) => void;
	onAddTag: (tag: string) => void;
	onRemoveTag: (tag: string) => void;
	onResetToDefault: () => void;
	onSaveDraft: () => void;
}

const TABS: { id: DrawerTab; label: string }[] = [
	{ id: "style", label: LABELS.drawerStyleTab },
	{ id: "history", label: LABELS.drawerHistoryTab },
	{ id: "context", label: LABELS.drawerContextTab },
	{ id: "validation", label: LABELS.drawerValidationTab },
];

function splitKey(key: string): { parent: string; leaf: string } {
	const lastDot = key.lastIndexOf(".");
	if (lastDot < 0) return { parent: "", leaf: key };
	return { parent: key.slice(0, lastDot), leaf: key.slice(lastDot + 1) };
}

export default function EditDrawer({
	langs,
	open,
	entry,
	activeTab,
	scope,
	palette,
	glossary,
	dirty,
	saving,
	onClose,
	onTabChange,
	onEntryChange,
	onRevert,
	onAddTag,
	onRemoveTag,
	onResetToDefault,
	onSaveDraft,
}: Props) {
	const validations = entry ? validateEntry(entry as StationsTextEntry, glossary) : [];
	const errorCount = validations.filter((v) => v.severity === "error").length;
	const warnCount = validations.filter((v) => v.severity === "warn").length;

	const { parent, leaf } = entry ? splitKey(entry.key) : { parent: "", leaf: "" };
	const showDefault = !!entry && !!entry.defaultText && entry.currentText !== entry.defaultText;

	return (
		<StyledDrawer
			anchor="right"
			open={open}
			onClose={onClose}
			variant="persistent">
			<DrawerHeader>
				<DrawerTitleGroup>
					{parent && (
						<Tooltip
							title={entry?.key || ""}
							placement="bottom-start">
							<DrawerKeyBreadcrumb>{parent}</DrawerKeyBreadcrumb>
						</Tooltip>
					)}
					<DrawerTitle title={entry?.key || ""}>{leaf || "—"}</DrawerTitle>
					{entry && (
						<DrawerStatusRow>
							<StatusChip status={entry.status}>{entry.status}</StatusChip>
							<span>·</span>
							<span>{entry.screen}</span>
							<span>·</span>
							<span>{entry.group}</span>
						</DrawerStatusRow>
					)}
				</DrawerTitleGroup>
				<DrawerCloseBtn
					onClick={onClose}
					title="Close (Esc)">
					<i className="fa fa-times" />
				</DrawerCloseBtn>
			</DrawerHeader>

			{entry && (
				<>
					{showDefault && (
						<DrawerDefaultBlock>
							<strong>Default text (from code)</strong>
							{entry.defaultText}
						</DrawerDefaultBlock>
					)}

					<div style={{ padding: "12px 20px", borderBottom: "1px solid #e2e8f0" }}>
						<TextField
							fullWidth
							multiline
							minRows={3}
							maxRows={8}
							variant="outlined"
							size="small"
							label={LABELS.drawerTextEn}
							placeholder={entry.defaultText || ""}
							value={entry.currentText}
							onChange={(e) => onEntryChange({ currentText: e.target.value })}
						/>
						{langs.map((lang) => (
							<div
								key={lang}
								style={{ marginTop: 10 }}>
								<TextField
									fullWidth
									multiline
									minRows={3}
									maxRows={8}
									variant="outlined"
									size="small"
									inputProps={{ lang }}
									label={LABELS.langName(lang)}
									helperText={LABELS.drawerTextViHint}
									value={String(entry.translations?.[lang] || "")}
									onChange={(e) =>
										onEntryChange({
											translations: { ...(entry.translations || {}), [lang]: e.target.value },
										})
									}
								/>
							</div>
						))}
						<div style={{ marginTop: 10 }}>
							<div
								style={{
									fontSize: "0.625rem",
									fontWeight: 700,
									color: "#64748b",
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									marginBottom: 6,
								}}>
								Tags
							</div>
							<TagsCell
								tags={entry.tags || []}
								onAdd={onAddTag}
								onRemove={onRemoveTag}
								onFilterByTag={() => undefined}
							/>
						</div>
					</div>

					<DrawerTabBar>
						{TABS.map((tab) => (
							<DrawerTabBtn
								key={tab.id}
								active={activeTab === tab.id}
								onClick={() => onTabChange(tab.id)}>
								{tab.label}
								{tab.id === "validation" && validations.length > 0 && (
									<span
										style={{
											marginLeft: 4,
											fontSize: "0.6875rem",
											background: errorCount > 0 ? "#dc2626" : "#b45309",
											color: "#fff",
											borderRadius: 8,
											padding: "0 5px",
										}}>
										{errorCount + warnCount}
									</span>
								)}
							</DrawerTabBtn>
						))}
					</DrawerTabBar>

					<DrawerBody>
						{activeTab === "style" && (
							<DrawerStyleTab
								entry={entry}
								palette={palette}
								onChange={onEntryChange}
							/>
						)}
						{activeTab === "history" && (
							<DrawerHistoryTab
								entryKey={entry.key}
								scope={scope}
								onRevert={onRevert}
							/>
						)}
						{activeTab === "context" && (
							<DrawerContextTab
								entry={entry}
								scope={scope}
							/>
						)}
						{activeTab === "validation" && <DrawerValidationPanel results={validations} />}
					</DrawerBody>

					<DrawerFooter>
						<DrawerFooterBtn
							danger
							disabled={!entry.defaultText || entry.currentText === entry.defaultText}
							onClick={onResetToDefault}
							title="Reset current text to default">
							Reset to default
						</DrawerFooterBtn>
						<DrawerFooterBtn onClick={onClose}>Close</DrawerFooterBtn>
						<DrawerFooterBtn
							primary
							disabled={!dirty || saving}
							onClick={onSaveDraft}>
							{saving ? "Saving…" : "Save Draft"}
						</DrawerFooterBtn>
					</DrawerFooter>
				</>
			)}
		</StyledDrawer>
	);
}
